/**
 * HeaderBell — the ONE attention surface in the header (PR 8, plan D1/F1/F5).
 *
 * It replaced three controls that each counted the same rows differently: the
 * "Notifications 75" link (one row per producer call), the business Alerts
 * pill, and the amber "Needs attention" health pill. Now there is one bell
 * with a dropdown of three sections, assembled per person by the server
 * (GET /api/notifications, backend/notifications/feed.py):
 *
 *   For you  approvals waiting, my pending changes, business alerts
 *   Data     only feeds whose lateness changes a number ("Ad data is 2 days late")
 *   System   admins only: open incidents, one line per source
 *
 * Engineering events reach a non-admin only as one rolled-up "Some data may
 * be delayed" item — the server's severity gate, not this file's. Each item
 * deep-links to its fix (or opens the Pending changes tray); "Mark all read"
 * clears the badge for this person only; the gear flips the panel to the
 * preferences view (what reaches my bell / my email) — inside the dropdown,
 * no new page (owner decision 6).
 *
 * Polling: one shared 60 s query, paused while the browser tab is hidden
 * (useApiQuery) — never a setInterval. The shared /api/health probe is read
 * here too: three misses in a row put a red "!" on the bell and a top item
 * saying ClickPay can't reach its server (the old health pill's rule).
 *
 * Nothing underlying is lost with the health pill: the Data section's footer
 * keeps the PPC refresh chip for everyone, and admins get the old pill's
 * "Systems check" rows (BackendStatus with Start/Restart, FreshnessBadge,
 * PpcFreshnessChip, SystemStatusDot) under the System section — the same
 * components, mounted unchanged, only while that disclosure is open.
 *
 * Behaviour comes from the overlay kit (components/overlay/useOverlay.ts):
 * Escape and an outside click close it, focus returns to the bell, and the
 * arrow keys move between items. The views below are pure (props in, markup
 * out) so HeaderBell.test.tsx renders them without a DOM.
 */
import { useCallback, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Bell, ChevronDown, ChevronRight, Loader2, Settings, WifiOff } from 'lucide-react'
import { useAuth } from '../../lib/auth'
import { formatRelative } from '../../lib/format'
import { cn } from '../../lib/cn'
import {
  notificationsApi, type NotificationCategory, type NotificationFeed, type NotificationItem,
  type NotificationPrefs, type NotificationSection,
} from '../../lib/notificationsApi'
import { keys } from '../../lib/query/keys'
import { invalidatePrefix } from '../../lib/query/invalidate'
import { useApiQuery } from '../../lib/query/useApiQuery'
import { useBackendHealth } from '../../lib/query/freshness'
import { useConsecutiveFailures } from '../../lib/query/failures'
import { nextMenuIndex, useOverlay } from '../overlay/useOverlay'
import BackendStatus from '../BackendStatus'
import FreshnessBadge from '../FreshnessBadge'
import PpcFreshnessChip from '../PpcFreshnessChip'
import SystemStatusDot from '../SystemStatusDot'
import { settingsHref } from '../../pages/settings/routing'
import {
  BACKEND_DOWN_TEXT, BADGE_CLASS, PROBE_FAIL_ESCALATE, SEVERITY_DOT, activateItem, badgeText,
  badgeTone, bellLabel, countLabel, itemWhen, markAllThenRefetch, prefRows,
} from './bellModel'

const FEED_POLL_MS = 60_000
const PANEL_ID = 'header-bell-panel'

type PanelView = 'list' | 'prefs'
type PrefsDraft = Pick<NotificationPrefs, 'bell' | 'email'>

export default function HeaderBell() {
  const { isAdmin: authAdmin } = useAuth()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<PanelView>('list')
  const [marking, setMarking] = useState(false)
  const [draft, setDraft] = useState<PrefsDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [prefsError, setPrefsError] = useState<string | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const feedQ = useApiQuery(keys.notifications.feed(), notificationsApi.feed, {
    poll: FEED_POLL_MS, keepPrevious: true,
  })
  const prefsQ = useApiQuery(keys.notifications.prefs(), notificationsApi.getPrefs, {
    enabled: open && view === 'prefs',
  })
  // The shared liveness probe (one timer app-wide); the bell only reads how
  // many polls in a row it has missed.
  useBackendHealth()
  const backendDown = useConsecutiveFailures(keys.health()) >= PROBE_FAIL_ESCALATE

  const feed = feedQ.data ?? null
  const isAdmin = feed?.viewer.is_admin ?? authAdmin
  const { refetch } = feedQ

  const close = useCallback(() => {
    setOpen(false)
    setView('list')
    setDraft(null)
    setPrefsError(null)
  }, [])

  useOverlay(open, panelRef, {
    onClose: close,
    anchorRef: buttonRef,
    onKey: (e) => moveItemFocus(panelRef, e),
  })

  const refresh = useCallback(() => {
    void invalidatePrefix(client, keys.notifications.all())
    void refetch()
  }, [client, refetch])

  function onItem(item: NotificationItem) {
    activateItem(item, {
      markRead: notificationsApi.markRead,
      navigate: (href) => navigate(href),
      dispatch: (event) => window.dispatchEvent(event),
      close,
      refresh,
    })
  }

  async function onMarkAll() {
    setMarking(true)
    try {
      await markAllThenRefetch(notificationsApi.markAllRead, refetch)
    } catch {
      // The badge simply stays as it was; the next poll tells the truth.
    } finally {
      setMarking(false)
    }
  }

  function openPrefs() {
    setDraft(null)
    setPrefsError(null)
    setView('prefs')
  }

  const prefs = prefsQ.data ?? null
  const shownDraft: PrefsDraft | null = draft ?? (prefs ? { bell: prefs.bell, email: prefs.email } : null)

  function toggle(channel: 'bell' | 'email', category: NotificationCategory) {
    if (!shownDraft) return
    setDraft({ ...shownDraft, [channel]: { ...shownDraft[channel], [category]: !shownDraft[channel][category] } })
  }

  async function savePrefs() {
    if (!shownDraft) return
    setSaving(true)
    setPrefsError(null)
    try {
      await notificationsApi.savePrefs(shownDraft)
      setDraft(null)
      setView('list')
      refresh()
    } catch (e) {
      setPrefsError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative">
      <BellButtonView
        buttonRef={buttonRef}
        unread={feed?.unread ?? 0}
        worst={feed?.worst_unread ?? null}
        backendDown={backendDown}
        open={open}
        onClick={() => (open ? close() : setOpen(true))}
      />
      {open && (
        <div
          ref={panelRef}
          id={PANEL_ID}
          role="dialog"
          aria-label="Notifications"
          tabIndex={-1}
          className="absolute right-0 top-full mt-1 w-[26rem] max-md:fixed max-md:inset-x-2 max-md:top-auto max-md:w-auto max-h-[75vh] overflow-y-auto bg-overlay border border-edge rounded-lg shadow-pop z-50 outline-none"
        >
          {view === 'prefs' ? (
            <BellPrefsView
              prefs={prefs}
              draft={shownDraft}
              isAdmin={isAdmin}
              loading={prefsQ.isPending}
              loadError={prefsQ.isError}
              saving={saving}
              error={prefsError}
              onToggle={toggle}
              onSave={() => void savePrefs()}
              onBack={() => setView('list')}
            />
          ) : (
            <BellPanelView
              feed={feed}
              loading={feedQ.isPending}
              loadError={feedQ.isError && !feed}
              onRetry={() => void refetch()}
              backendDown={backendDown}
              isAdmin={isAdmin}
              marking={marking}
              onItem={onItem}
              onMarkAll={() => void onMarkAll()}
              onPrefs={openPrefs}
              onNavigate={close}
              backendRow={isAdmin ? <BackendStatus /> : null}
              dataFooter={<FooterRow label="Ad data"><PpcFreshnessChip /></FooterRow>}
              systemFooter={isAdmin ? <SystemsCheck onNavigate={close} /> : null}
            />
          )}
        </div>
      )}
    </div>
  )
}

/** ArrowDown / ArrowUp / Home / End move between the bell's items. */
function moveItemFocus(panelRef: RefObject<HTMLDivElement | null>, e: KeyboardEvent): boolean {
  const panel = panelRef.current
  if (!panel) return false
  const items = Array.from(panel.querySelectorAll<HTMLElement>('[data-bell-item]'))
  const next = nextMenuIndex(items.indexOf(document.activeElement as HTMLElement), items.length, e.key)
  if (next === null) return false
  e.preventDefault()
  items[next].focus()
  return true
}

/* ── Views (pure: props in, markup out) ──────────────────────────────────── */

export function BellButtonView({
  unread, worst, backendDown, open, onClick, buttonRef,
}: {
  unread: number
  worst: NotificationFeed['worst_unread']
  backendDown: boolean
  open: boolean
  onClick: () => void
  buttonRef?: RefObject<HTMLButtonElement | null>
}) {
  const text = backendDown ? '!' : badgeText(unread)
  const tone = backendDown ? 'danger' : badgeTone(worst)
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      data-testid="header-bell"
      aria-label={bellLabel(unread, backendDown)}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? PANEL_ID : undefined}
      title={bellLabel(unread, backendDown)}
      className="relative inline-grid place-items-center h-8 w-8 rounded-md border border-edge bg-surface text-ink-secondary hover:bg-surface-2 hover:text-ink"
    >
      <Bell size={15} aria-hidden />
      {text && (
        <span
          data-testid="header-bell-badge"
          className={cn(
            'absolute -top-1.5 -right-1.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-semibold leading-[1.1rem] text-center tabular-nums',
            BADGE_CLASS[tone],
          )}
        >
          {text}
        </span>
      )}
    </button>
  )
}

export type BellPanelViewProps = {
  feed: NotificationFeed | null
  loading: boolean
  loadError: boolean
  onRetry: () => void
  backendDown: boolean
  isAdmin: boolean
  marking: boolean
  onItem: (item: NotificationItem) => void
  onMarkAll: () => void
  onPrefs: () => void
  /** A link inside the panel was followed: close it. */
  onNavigate?: () => void
  /** Admins: Start/Restart backend, shown under the "can't reach" item. */
  backendRow?: ReactNode
  /** Everyone: the PPC data chip (the refresh trigger the health pill held). */
  dataFooter?: ReactNode
  /** Admins: the Systems check disclosure. */
  systemFooter?: ReactNode
}

export function BellPanelView({
  feed, loading, loadError, onRetry, backendDown, isAdmin, marking, onItem, onMarkAll, onPrefs,
  onNavigate, backendRow, dataFooter, systemFooter,
}: BellPanelViewProps) {
  const sections = feed?.sections ?? []
  const hasData = sections.some((s) => s.id === 'data')
  const hasSystem = sections.some((s) => s.id === 'system')
  const anyUnread = sections.some((s) => s.items.some((i) => i.unread))
  return (
    <div className="text-[13px] text-ink">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-edge bg-overlay px-3 py-2">
        <h2 className="text-sm font-semibold text-ink">Notifications</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMarkAll}
            disabled={marking || !anyUnread}
            className="rounded-md px-2 py-1 text-xs font-medium text-accent hover:bg-accent-soft disabled:opacity-50 disabled:hover:bg-transparent"
          >
            {marking ? <Loader2 size={12} className="inline animate-spin" aria-hidden /> : null} Mark all read
          </button>
          <button
            type="button"
            onClick={onPrefs}
            aria-label="Notification settings"
            title="Choose what reaches your bell and email"
            className="inline-grid place-items-center h-7 w-7 rounded-md text-ink-muted hover:bg-surface-3 hover:text-ink"
          >
            <Settings size={14} aria-hidden />
          </button>
        </div>
      </div>

      {backendDown && (
        <div role="alert" className="border-b border-danger-edge bg-danger-soft px-3 py-2.5 text-danger-soft-fg">
          <div className="flex items-start gap-2">
            <WifiOff size={14} className="mt-0.5 shrink-0" aria-hidden />
            <span className="font-medium">{BACKEND_DOWN_TEXT}</span>
          </div>
          {backendRow && <div className="mt-2 pl-6">{backendRow}</div>}
        </div>
      )}

      {!feed && loading && (
        <div className="flex items-center gap-2 px-3 py-6 text-ink-muted">
          <Loader2 size={14} className="animate-spin" aria-hidden /> Checking…
        </div>
      )}
      {!feed && loadError && (
        <div className="px-3 py-6 text-ink-muted">
          Notifications could not load.{' '}
          <button type="button" onClick={onRetry} className="font-medium text-accent hover:underline">Try again</button>
        </div>
      )}

      {sections.map((s) => (
        <SectionView key={s.id} section={s} onItem={onItem} isAdmin={isAdmin} onNavigate={onNavigate}
                     footer={s.id === 'data' ? dataFooter : s.id === 'system' ? systemFooter : null} />
      ))}

      {/* A section switched off in preferences still keeps its tools reachable. */}
      {feed && ((!hasData && dataFooter) || (!hasSystem && systemFooter)) && (
        <div className="border-t border-edge px-3 py-2 space-y-2">
          {!hasData && dataFooter}
          {!hasSystem && systemFooter}
        </div>
      )}
    </div>
  )
}

function SectionView({
  section, onItem, isAdmin, onNavigate, footer,
}: {
  section: NotificationSection
  onItem: (item: NotificationItem) => void
  isAdmin: boolean
  onNavigate?: () => void
  footer?: ReactNode
}) {
  return (
    <section className="border-b border-edge-subtle last:border-b-0" data-bell-section={section.id}>
      <h3 className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
        {section.label}
      </h3>
      {section.items.length === 0 ? (
        <p className="px-3 pb-2.5 text-xs text-ink-faint">{section.empty}</p>
      ) : (
        <ul role="menu" aria-label={section.label} className="pb-1">
          {section.items.map((item) => (
            <li key={item.key} role="none">
              <ItemView item={item} onItem={onItem} />
            </li>
          ))}
        </ul>
      )}
      {section.more > 0 && (
        <p className="px-3 pb-2 text-xs text-ink-muted">
          {section.more} more not shown.
          {section.id === 'system' && isAdmin && (
            <> <Link to={settingsHref('notifications')} onClick={onNavigate} className="text-accent hover:underline">See them all</Link></>
          )}
        </p>
      )}
      {footer && <div className="px-3 pb-2.5">{footer}</div>}
    </section>
  )
}

function ItemView({ item, onItem }: { item: NotificationItem; onItem: (item: NotificationItem) => void }) {
  const times = countLabel(item.count)
  const when = itemWhen(item)
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      data-bell-item={item.key}
      data-unread={item.unread ? 'true' : 'false'}
      onClick={() => onItem(item)}
      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
    >
      <span
        aria-hidden
        className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', item.unread ? SEVERITY_DOT[item.severity] : 'bg-transparent')}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block', item.unread ? 'font-semibold text-ink' : 'text-ink-secondary')}>
          {item.unread && <span className="sr-only">Unread: </span>}
          {item.title}
        </span>
        {item.detail && <span className="mt-0.5 block line-clamp-2 text-xs text-ink-muted">{item.detail}</span>}
        <span className="mt-1 flex items-center gap-2 text-[11px] text-ink-faint">
          {when && <span>{when.prefix}{formatRelative(when.at)}</span>}
          {times && <span className="rounded-full bg-surface-3 px-1.5 py-px text-ink-secondary">{times}</span>}
        </span>
      </span>
    </button>
  )
}

/** One labelled footer row — the label is plain English, the child an
 *  existing component mounted unchanged. */
function FooterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 pt-1">
      <span className="text-[11px] text-ink-muted shrink-0">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/** Admins: the old health pill's rows, mounted only while expanded. */
function SystemsCheck({ onNavigate }: { onNavigate: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-md border border-edge-subtle">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-ink-secondary hover:text-ink"
      >
        {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
        Systems check
      </button>
      {open && (
        <div className="space-y-2.5 border-t border-edge-subtle px-2 py-2">
          <FooterRow label="Backend"><BackendStatus /></FooterRow>
          <FooterRow label="Data feeds"><FreshnessBadge /></FooterRow>
          <FooterRow label="PPC data"><PpcFreshnessChip /></FooterRow>
          <FooterRow label="Subsystems"><SystemStatusDot /></FooterRow>
          <Link
            to={settingsHref('system')}
            onClick={onNavigate}
            className="block pt-1 text-center text-xs text-accent hover:text-accent-hover"
          >
            Open System health →
          </Link>
        </div>
      )}
    </div>
  )
}

export type BellPrefsViewProps = {
  prefs: NotificationPrefs | null
  draft: Pick<NotificationPrefs, 'bell' | 'email'> | null
  isAdmin: boolean
  loading: boolean
  loadError: boolean
  saving: boolean
  error: string | null
  onToggle: (channel: 'bell' | 'email', category: NotificationCategory) => void
  onSave: () => void
  onBack: () => void
}

/** "What reaches me": two columns of switches, inside the dropdown. */
export function BellPrefsView({
  prefs, draft, isAdmin, loading, loadError, saving, error, onToggle, onSave, onBack,
}: BellPrefsViewProps) {
  const emailOn = !!prefs?.email_available
  return (
    <div className="text-[13px] text-ink" data-bell-view="prefs">
      <div className="flex items-center gap-2 border-b border-edge px-3 py-2">
        <button type="button" onClick={onBack} aria-label="Back to notifications"
                className="inline-grid place-items-center h-7 w-7 rounded-md text-ink-muted hover:bg-surface-3 hover:text-ink">
          <ArrowLeft size={14} aria-hidden />
        </button>
        <h2 className="text-sm font-semibold text-ink">Notification settings</h2>
      </div>
      {!draft ? (
        <div className="px-3 py-6 text-ink-muted">
          {loadError ? 'Your settings could not load.' : loading ? 'Loading your settings…' : null}
        </div>
      ) : (
        <div className="px-3 py-2">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <th className="py-1 font-medium">Notify me about</th>
                <th className="py-1 px-2 text-center font-medium">Show in my bell</th>
                <th className="py-1 px-2 text-center font-medium">Email me</th>
              </tr>
            </thead>
            <tbody>
              {prefRows(isAdmin).map((r) => (
                <tr key={r.category} className="border-t border-edge-subtle" data-pref={r.category}>
                  <td className="py-1.5 pr-2">
                    <span className="block text-ink">{r.label}</span>
                    <span className="block text-[11px] text-ink-muted">{r.hint}</span>
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <input type="checkbox" aria-label={`${r.label}: show in my bell`}
                           checked={draft.bell[r.category]} onChange={() => onToggle('bell', r.category)}
                           className="accent-[var(--color-accent)]" />
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <input type="checkbox" aria-label={`${r.label}: email me`}
                           checked={emailOn && draft.email[r.category]} disabled={!emailOn}
                           onChange={() => onToggle('email', r.category)}
                           className="accent-[var(--color-accent)]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!emailOn && prefs?.email_note && (
            <p className="mt-2 text-xs text-ink-muted" data-email-note>{prefs.email_note}</p>
          )}
          {error && (
            <p role="alert" className="mt-2 rounded-md border border-danger-edge bg-danger-soft px-2 py-1 text-xs text-danger-soft-fg">
              {error}
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={onBack}
                    className="rounded-md px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-3">
              Cancel
            </button>
            <button type="button" onClick={onSave} disabled={saving}
                    className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
